import { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useQueue } from '../contexts/QueueContext';

const formatDuration = (seconds) => {
  const value = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(value / 60);
  const remainder = Math.floor(value % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const UpNextDrawer = () => {
  const [open, setOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const {
    queue,
    currentIndex,
    currentTrack,
    queueLength,
    removeFromQueue,
    reorderQueue,
    setPlayingIndex,
    clearQueue,
    shuffle,
    repeatMode,
    autoplay,
    toggleShuffle,
    cycleRepeat,
    setAutoplay,
  } = useQueue();

  const remainingDuration = useMemo(() => queue
    .slice(Math.max(currentIndex + 1, 0))
    .reduce((total, item) => total + (Number(item.duration) || 0), 0), [currentIndex, queue]);

  if (!queueLength) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-[70] hidden rounded-full border border-white/10 bg-black/90 px-4 py-3 text-sm font-semibold text-white shadow-2xl backdrop-blur md:block"
        aria-label={`Open Up Next queue with ${queueLength} items`}
      >
        Up Next · {queueLength}
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/55" role="presentation" onMouseDown={() => setOpen(false)}>
          <aside
            className="flex h-full w-full max-w-md flex-col border-l border-white/10 bg-zinc-950 text-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Up Next queue"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="border-b border-white/10 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-400">Playing Queue</p>
                  <h2 className="mt-1 text-xl font-black">Up Next</h2>
                  <p className="mt-1 text-xs text-zinc-400">
                    {queueLength} items · {formatDuration(remainingDuration)} remaining
                  </p>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 text-xl text-zinc-300 hover:bg-white/10" aria-label="Close queue">×</button>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <button type="button" onClick={toggleShuffle} aria-pressed={shuffle} className={`rounded-lg border px-3 py-2 text-xs font-bold ${shuffle ? 'border-orange-400 bg-orange-400/15 text-orange-300' : 'border-white/10 text-zinc-300'}`}>Shuffle</button>
                <button type="button" onClick={cycleRepeat} className={`rounded-lg border px-3 py-2 text-xs font-bold ${repeatMode !== 'off' ? 'border-orange-400 bg-orange-400/15 text-orange-300' : 'border-white/10 text-zinc-300'}`}>Repeat: {repeatMode}</button>
                <button type="button" onClick={() => setAutoplay(!autoplay)} aria-pressed={autoplay} className={`rounded-lg border px-3 py-2 text-xs font-bold ${autoplay ? 'border-orange-400 bg-orange-400/15 text-orange-300' : 'border-white/10 text-zinc-300'}`}>Autoplay</button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-3">
              {queue.map((item, index) => {
                const active = index === currentIndex;
                return (
                  <div
                    key={item.track_id}
                    draggable
                    onDragStart={() => setDraggedIndex(index)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (draggedIndex !== null) reorderQueue(draggedIndex, index);
                      setDraggedIndex(null);
                    }}
                    className={`mb-2 flex items-center gap-3 rounded-xl border p-2 ${active ? 'border-orange-400/60 bg-orange-400/10' : 'border-white/5 bg-white/[0.03]'}`}
                  >
                    <button type="button" onClick={() => setPlayingIndex(index)} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-label={`Play ${item.title}`}>
                      <div className="h-12 w-12 flex-none overflow-hidden rounded-lg bg-zinc-800">
                        {item.cover ? <img src={item.cover} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-lg">♪</div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-sm font-bold">{item.title || 'Untitled'}</p>
                          {item.autoplay_added && (
                            <span className="inline-flex flex-none items-center gap-1 rounded-full border border-orange-400/30 bg-orange-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300">
                              <Sparkles className="h-2.5 w-2.5" /> Autoplay
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-zinc-400">{item.artist || item.track_type}</p>
                        {item.autoplay_added && item.recommendation_reason && (
                          <p className="mt-0.5 truncate text-[10px] text-orange-200/70">{item.recommendation_reason}</p>
                        )}
                      </div>
                      <span className="text-xs tabular-nums text-zinc-500">{formatDuration(item.duration)}</span>
                    </button>
                    <button type="button" onClick={() => removeFromQueue(item.track_id)} className="rounded-full p-2 text-zinc-500 hover:bg-white/10 hover:text-white" aria-label={`Remove ${item.title} from queue`}>×</button>
                  </div>
                );
              })}
            </div>

            <footer className="flex items-center justify-between border-t border-white/10 p-4">
              <p className="max-w-[70%] truncate text-xs text-zinc-400">Now playing: {currentTrack?.title || 'Nothing selected'}</p>
              <button type="button" onClick={clearQueue} className="text-xs font-bold text-red-300 hover:text-red-200">Clear queue</button>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
};

export { formatDuration };
export default UpNextDrawer;
